import React, { useState, useEffect, useRef } from 'react';
import BegegnungVisual from "@/components/begegnung/BegegnungVisual";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { KlickTippOptin } from '@/components/KlickTippOptin';
import CoachMarker from '@/components/CoachMarker';
import { BRAND } from '@/config/brand';
import BrueckensatzBlock from '@/components/BrueckensatzBlock';
import BrueckensatzToggle from '@/components/BrueckensatzToggle';
import AnliegenSkeleton from '@/components/AnliegenSkeleton';
import HeartPiece from '@/components/HeartPiece';
import ConfidenceBadge from '@/components/ConfidenceBadge';
import AnliegenCreditCounter from '@/components/AnliegenCreditCounter';
import CockpitSidebar from '@/components/CockpitSidebar';
// Sprint V12 Fix 1 (Boardroom-CoS Sa 05.09.2026-18 Uhr) — Free-Cockpit Credits-Engine
import FreeAnliegenFlow from '@/components/redesign/FreeAnliegenFlow';
// Sprint V19 Schicht 2 Stufe 2 (Boardroom-CoS Di 08.09.2026-13 Uhr) — Folge-Frage generisch
import FolgeFrage from '@/components/redesign/FolgeFrage';
import { Loader2 } from 'lucide-react';
import { isPremium as _isPremium } from '@/lib/licensing';
import { SafetyNotice } from '@/components/SafetyNotice';
import { KernAntwort } from '@/components/KernAntwort';
// Sprint V12 Fix 2 (Boardroom-CoS Sa 05.09.2026-18 Uhr) — StarModellHelfer aus
// Anliegen-Flow entfernt (6-Ebenen-Framework gehoert NUR in PageWirkungGestalten).
// import StarModellHelfer from '@/components/redesign/StarModellHelfer';

const FOKUSTYP_LABEL = { katze: 'Katze', eule: 'Eule', biene: 'Biene', hund: 'Hund' };

// Sprint V1.7 Launch-Conversion-QW#1 (Do10.09.2026, 17:45 Europe/Berlin) —
// Kontextsensitive Loading-Phasen. Rotiert alle ~2.5s durch drei Phasen
// waehrend generating=true. Reduziert das Gefuehl von „tot" waehrend LLM.
function AnliegenLoadingPhase() {
  const PHASES = [
    'Analysiere dein Anliegen …',
    'Erkenne Fokustyp …',
    'Generiere Flexpfad …',
  ];
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t1 = setTimeout(() => setIdx(1), 2500);
    const t2 = setTimeout(() => setIdx(2), 5500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <span data-testid="anliegen-loading-phase" data-phase={idx}>
      {PHASES[idx]}
    </span>
  );
}

// Sprint 4 Issue 3.2 — 4 anklickbare Kontext-Karten mit Helper-Text.
// „Alltag" als 4. Karte ergänzt (Nachbarn, Service, Behörden, Handwerker).
const KONTEXT_KARTEN = [
  {
    id: 'privat',
    label: 'Privat',
    helper: 'Mit deinem Partner, deiner Partnerin, einem Freund oder einer Freundin',
    beispiel: "Mein Mann will nicht mit uns wegfahren. Er blockt ab, sagt 'müssen wir mal sehen'.",
  },
  {
    id: 'beruflich',
    label: 'Beruflich',
    helper: 'Im Job, mit Kollegen, Vorgesetzten, Kunden oder im Team',
    beispiel: "Kollege versteht meine Idee nicht. Er fragt ständig 'warum' und 'wozu'.",
  },
  {
    id: 'familie',
    label: 'Familie',
    helper: 'Mit Eltern, Geschwistern, Kindern oder im Familienkreis',
    beispiel: 'Mein Kind verdreht die Augen, wenn ich was Praktisches frage.',
  },
  {
    id: 'alltag',
    label: 'Alltag',
    helper: 'Mit Nachbarn, im Service, bei Behörden, Handwerkern oder im Alltag',
    beispiel: 'Der Handwerker sagt zu, kommt aber nicht. Ich erreiche ihn nicht.',
  },
];

// Sprint 4 Issue 3.3 — Verdacht-Helper-Text pro Fokustyp (typische Verhaltensweisen).
const VERDACHT_HELPER_BY_FOKUSTYP = {
  katze:
    "Das zeigt sich in: Widerstand, kein innerer Antrieb, 'müssen wir mal sehen', Rückzug.",
  eule:
    "Das zeigt sich in: Hinterfragen, Sinn-Frage, 'warum', 'wozu', 'das bringt doch nichts'.",
  biene:
    "Das zeigt sich in: Perfektionismus, Nachfragen, 'wie genau', 'was meinst du mit', Unklarheit.",
  hund:
    "Das zeigt sich in: Rückzug, emotional, 'fühl mich nicht gehört', Suche nach Bestätigung.",
};

// Sprint V28 Issue H (Fr 27.02.2026) — Andreas-Differenzierungs-Leitlinie:
// Free-User bekommt jetzt fokustyp-spezifische Handlungs-Empfehlung, nicht
// nur die generische „Anteil dominiert"-Begruendung. Direkte Uebernahme
// aus Andreas' Sales-Direktive (Cluster-Doc, Issue H):
//   HUND  → Beziehung ZUERST, dann Inhalt. NICHT Bedenkzeit (=Zurueckweisung).
//   KATZE → Reiz-Analyse, was koennte reizen? NICHT Bedenkzeit (=Rueckzug).
//   BIENE → Konkrete Schritte, explizit sein. Vage Wuensche kommen nicht an.
//   EULE  → Sinn auf den Punkt, Meinung in einem Satz. Nur den Kern.
// Mit Pro-Upgrade-Hinweis, damit Sales-Hebel intakt bleibt.
const FREE_HANDLUNGS_TIPP_BY_FOKUSTYP = {
  hund: {
    do: 'Sprich die Beziehungs-Ebene zuerst an. „Mir ist wichtig, dass es zwischen uns passt — deshalb möchte ich das mit dir klären." Danach erst zum Inhalt.',
    dont: 'Sag NICHT „lass uns eine Nacht drüber schlafen" — die Hund-Person interpretiert Bedenkzeit als Zurückweisung und zieht sich zurück.',
  },
  katze: {
    do: 'Frag dich zuerst: Was könnte für sie darin reizvoll sein? Was könnte stören? Sprich zuerst über den Reiz, dann über den Preis. Kein Druck, keine „du musst"-Sätze.',
    dont: 'Vermeide Bedenkzeit-Angebote — die Katze-Person nutzt sie zum passiven Wegducken statt zur Auseinandersetzung.',
  },
  biene: {
    do: 'Nenne konkrete Schritte in Reihenfolge: „Erstens X, zweitens Y, drittens Z." Sei explizit, was du erwartest — vage Wünsche kommen bei der Biene nicht an.',
    dont: 'Sag NICHT „mach\u0027s einfach irgendwie" oder „such dir was aus" — das erzeugt Stillstand statt Bewegung.',
  },
  eule: {
    do: 'Bring deinen Sinn/deinen Kern auf einen Satz. Deine Meinung, dein „darum": knapp, klar, ohne Ausschmückung. Die Eule folgt dem Kern, nicht dem Beiwerk.',
    dont: 'Erklär nicht zu lang — jede zusätzliche Begründung schwächt deinen Punkt und lädt die Eule zum Zerlegen ein.',
  },
};

// Sprint 4d AC #234 — Textarea-Limit von 400 auf 1000 Zeichen (Wispr-Diktat braucht Platz).
const MAX_LEN = 1000;

const STATIC_HINT = '💡 Mündlich, direkt aussprechbar';

export default function DashboardAnliegen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sprint 4c-2 A.9/A.10 — Tier-Info für conditional Rendering (Newsletter / Upsell).
  // Refactor Feb 2026: Single source of truth via /app/frontend/src/lib/licensing.js.
  // Backend liefert kanonisches `user.is_premium`-Flag aus /api/v2/user/dashboard.
  const [isPremium, setIsPremium] = useState(null); // null = noch nicht geladen
  useEffect(() => {
    let cancelled = false;
    api.get('/user/dashboard').then(
      ({ data }) => {
        if (cancelled) return;
        setIsPremium(_isPremium(data?.user));
      },
      () => { if (!cancelled) setIsPremium(false); /* Fetch-Fail → Free-Fallback (Upsell anzeigen) */ },
    );
    return () => { cancelled = true; };
  }, []);
  // tierLoaded: true sobald wir wissen, ob Premium oder Free.
  const tierLoaded = isPremium !== null;
  // isFreeUser: nur TRUE, wenn explizit kein Premium. Während loading → false (Upsell versteckt).
  const isFreeUser = tierLoaded && isPremium === false;

  // Sprint 4c-2 A.7 — Ref auf Textarea, um beim Case-C-Retry hochzuscrollen.
  const situationRef = useRef(null);

  // Step 1
  const [situation, setSituation] = useState('');
  const [kontextId, setKontextId] = useState(null); // Sprint 4 Issue 3.2 — anklickbare Kontext-Karte
  const [verdacht, setVerdacht] = useState(null); // { verdacht_fokustyp, verdacht_begruendung }
  const [sicherheit, setSicherheit] = useState(null);
  const [verdachtLoading, setVerdachtLoading] = useState(false);
  const [verdachtError, setVerdachtError] = useState(null);
  // Sprint V10 (Boardroom-CoS 3a) — User-Override, wenn Rückfrage beantwortet wurde.
  const [verdachtOverride, setVerdachtOverride] = useState(null);
  // Sprint V19 Schicht 2 Stufe 2 — Heuristik-Vorfilter + Folge-Frage Variante Alpha.
  const [folgeFrage, setFolgeFrage] = useState(null);
  const [heuristikDebug, setHeuristikDebug] = useState(null);

  // Retention AC-27 — URL-Prefill: ?q=anliegen&r=reaktion&c=kontext
  useEffect(() => {
    const q = searchParams.get('q');
    const r = searchParams.get('r');
    const c = searchParams.get('c');
    if (q || r || c) {
      const combined = [q, r].filter(Boolean).join(' ');
      if (combined) setSituation(combined.slice(0, 400));
      if (c && ['privat', 'beruflich', 'familie', 'alltag'].includes(c)) {
        setKontextId(c);
      }
      // URL aufräumen, damit ein Reload nicht erneut prefillt.
      const clean = new URLSearchParams(searchParams);
      clean.delete('q');
      clean.delete('r');
      clean.delete('c');
      setSearchParams(clean, { replace: true });
    }
    // POSTLAUNCH-V6 · P1-#4 — Personen-Rail Deep-Link: ?preset_name=X&preset_typ=Y
    const presetName = searchParams.get('preset_name');
    const presetTyp = searchParams.get('preset_typ');
    if (presetName || presetTyp) {
      const NAME_TO_LABEL = { hund: "Hund", eule: "Eule", katze: "Katze", biene: "Biene" };
      const typLabel = presetTyp && NAME_TO_LABEL[presetTyp] ? NAME_TO_LABEL[presetTyp] : "";
      const prefillParts = [];
      if (presetName) prefillParts.push(`Beziehung zu ${presetName}`);
      if (typLabel) prefillParts.push(`(Fokustyp: ${typLabel})`);
      if (prefillParts.length) setSituation(prefillParts.join(" ") + ". ");
      const clean = new URLSearchParams(searchParams);
      clean.delete('preset_name');
      clean.delete('preset_typ');
      setSearchParams(clean, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 2 — Präzise
  const [showPraezise, setShowPraezise] = useState(false);
  const [praeziseStep, setPraeziseStep] = useState(0); // 0 = none, 1 = logisch, 2 = emotional
  const [logisch, setLogisch] = useState('');
  const [emotional, setEmotional] = useState('');

  // Result
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [copiedFt, setCopiedFt] = useState(null);
  // Sprint 5a AC #3 — Saved-Library: lokaler Toggle-State pro Result-View.
  const [resultSaved, setResultSaved] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Sprint v1.7 Phase 3 (Do 03.09.2026) — Anliegen-Credit-Counter: refreshKey
  // triggert Neuladen des daily-usage nach jedem erfolgreichen Generate.
  const [creditRefreshKey, setCreditRefreshKey] = useState(0);
  const [creditLimitReached, setCreditLimitReached] = useState(false);

  const handleQuickPick = (text) => {
    setSituation(text.slice(0, MAX_LEN));
    // Sprint V12 Fix 3 — verdacht nur zuruecksetzen bei explizitem Neu-Ableiten,
    // nicht bei jeder Text-Aenderung (verhindert Flackern).
    setVerdachtError(null);
  };

  // Sprint 4 Issue 3.2 — Klick auf Kontext-Karte: selektieren + (falls Textarea leer) Beispiel-Text einfüllen.
  const handleKontextClick = (karte) => {
    setKontextId(karte.id);
    if (situation.trim().length === 0 && karte.beispiel) {
      setSituation(karte.beispiel.slice(0, MAX_LEN));
    }
    setVerdachtError(null);
  };

  const handleSituationChange = (e) => {
    const v = e.target.value.slice(0, MAX_LEN);
    setSituation(v);
    // Sprint V12 Fix 3 (Boardroom-CoS Sa 05.09.2026-18 Uhr) — Flackern behoben:
    // verdacht NICHT bei jedem Tastendruck zuruecksetzen, sonst unmounted/remounted
    // die Verdacht-Box bei jedem Zeichen (gefuehltes Flackern). Verdacht wird
    // stattdessen automatisch verworfen, wenn User erneut auf "Hinweise ableiten"
    // klickt oder situation.trim() zu kurz wird (Guard beim naechsten Ableiten).
  };

  const handleVerdacht = async () => {
    if (situation.trim().length < 3) {
      setVerdachtError('Bitte beschreibe die Situation kurz.');
      return;
    }
    setVerdachtError(null);
    setSicherheit(null);
    setVerdachtLoading(true);
    // Sprint V19 Schicht 2 Stufe 2 — Folge-Frage-Vorschlag parallel abrufen.
    // Nur fuer Pro-User (Endpoint gibt 403 fuer Free).
    if (!isFreeUser) {
      api.post('/anliegen/folge-frage-vorschlag', { situation: situation.trim() })
        .then((res) => {
          setHeuristikDebug(res.data?.heuristik || null);
          if (res.data?.folge_frage && res.data?.heuristik?.trigger_folge_frage) {
            setFolgeFrage(res.data.folge_frage);
          } else {
            setFolgeFrage(null);
          }
        })
        .catch(() => { setFolgeFrage(null); });
    }
    try {
      const res = await api.post('/anliegen/verdacht-preview', {
        situation: situation.trim(),
      });
      if (res.data?.status === 'sicherheit') {
        setSicherheit(res.data);
        setVerdacht(null);
        return;
      }
      setVerdacht(res.data);
      setVerdachtOverride(null); // Sprint V10 — Override bei neuem Ableiten zurücksetzen
      setShowPraezise(false);
      setPraeziseStep(0);
      setLogisch('');
      setEmotional('');
      // Sprint 4c-2 A.7 — Bei Case C (vage) zur Textarea hochscrollen,
      // damit klar ist, dass das Eingabefeld weiter oben editierbar bleibt.
      if (res.data?.case_label === 'C_vage') {
        setTimeout(() => {
          situationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          situationRef.current?.focus();
        }, 50);
      }
    } catch (err) {
      setVerdachtError(err?.response?.data?.detail || 'Verdacht konnte nicht abgeleitet werden.');
    } finally {
      setVerdachtLoading(false);
    }
  };

  const runGenerate = async (modus) => {
    setGenerateError(null);
    setGenerating(true);
    setResult(null);
    try {
      const payload = {
        situation: situation.trim(),
        modus,
        logische_komponente: modus === 'praezise' ? logisch.trim() : '',
        emotionale_komponente: modus === 'praezise' ? emotional.trim() : '',
      };
      // Sprint V10 (Boardroom-CoS 3a) — Rückfrage-Fallback: wenn User eine
      // Alternative bestätigt hat, übernehmen wir sie im Generate-Call.
      if (verdachtOverride) {
        payload.verdacht_override = verdachtOverride;
      }
      const res = await api.post('/anliegen/generate', payload);
      if (res.data?.status === 'sicherheit') {
        setSicherheit(res.data);
        setVerdacht(null);
        return;
      }
      setResult(res.data);
      // Sprint 5a AC #3 — Reset Save-State für die neue Result-View
      setResultSaved(false);
      setSaveError(null);
      // Sprint v1.7 Phase 3 — Counter refreshen nach erfolgreichem Anliegen
      setCreditRefreshKey((k) => k + 1);
      // Sprint 2 D5 — SessionStorage befüllen, damit das KlickTipp-Opt-in den
      // Verdacht (Fokustyp) als Hidden-Field mitsendet.
      try {
        if (res?.data?.verdacht_fokustyp) {
          sessionStorage.setItem('fokustyp_ergebnis', res.data.verdacht_fokustyp);
          sessionStorage.setItem('fokustyp_quiz_datum', new Date().toISOString());
        }
      } catch (e) { /* SessionStorage nicht verfügbar */ }
    } catch (err) {
      // Sprint v1.7 Phase 3 — 429 daily_limit_reached: Counter refreshen (zeigt Sperre),
      // Fehler NICHT im Formular anzeigen (Sperre-UI übernimmt das).
      if (err?.response?.status === 429) {
        setCreditRefreshKey((k) => k + 1);
        setCreditLimitReached(true);
      } else {
        setGenerateError(err?.response?.data?.detail || 'Generierung fehlgeschlagen.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (ft, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFt(ft);
      setTimeout(() => setCopiedFt((cur) => (cur === ft ? null : cur)), 1800);
    } catch {
      // noop
    }
  };

  const reset = () => {
    setSituation('');
    setKontextId(null);
    setVerdacht(null);
    setVerdachtError(null);
    setShowPraezise(false);
    setPraeziseStep(0);
    setLogisch('');
    setEmotional('');
    setResult(null);
    setGenerateError(null);
  };

  return (
    <div data-testid="anliegen-page" className="min-w-0 bg-[var(--fts-neutral-50)] dark:bg-slate-950">
      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Sprint 4c-3 C.3 — Coach-Marker (konsistent oben links auf jeder Coach-Seite) */}
        <CoachMarker testid="anliegen-coach-marker" className="mb-5" />

        <p
          data-testid="anliegen-eyebrow"
          className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2"
        >
          {BRAND.name} · Key
        </p>
        <h1
          data-testid="anliegen-title"
          className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3"
        >
          Lass uns die richtigen Worte finden.
        </h1>
        <p
          data-testid="anliegen-sub-headline"
          className="md:sticky md:top-14 z-10 bg-[var(--fts-neutral-50)]/95 dark:bg-slate-950/95 backdrop-blur py-2 text-slate-600 dark:text-slate-300 leading-relaxed mb-6 -mx-6 px-6 border-b border-slate-100 dark:border-slate-800"
        >
          {isFreeUser
            ? "Beschreib deine Situation — wir zeigen dir welcher Fokustyp gerade dominiert."
            : "Dein Kommunikations-Coach — schlägt den Flexpfad zum erkannten Fokustyp."}
        </p>

        {/* Sprint V12 Fix 1 (Boardroom-CoS Sa 05.09.2026-18 Uhr): Free-User bekommen
            die schlanke Credits-Engine (keine Heuristik-Zwischenschritte, keine
            Rueckfrage, kein Flexpfad-Integration, kein Flackern beim Tippen). */}
        {isFreeUser && <FreeAnliegenFlow />}

        {!isFreeUser && !result && (
          <section
            data-testid="anliegen-form"
            className="space-y-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6 shadow-sm"
          >
            {/* Sprint v1.7 Phase 3 (Do 03.09.2026) — A1 Anliegen-Credit-Counter.
                Nur Free-User sehen den Zähler (3/Tag). Pro-User: unlimited, kein UI-Element.
                Bei 3/3: Sperr-UI mit Pro-Vorteile-Teaser (Conversion-Hook). */}
            <AnliegenCreditCounter
              refreshKey={creditRefreshKey}
              onLimitReached={() => setCreditLimitReached(true)}
            />
            {/* POSTLAUNCH-V5 · P0-#6 — Vereinfachter Intro für Free (kürzer, klarer Wertversprechen).
                Pro-User sehen weiter das ausführliche Intro darunter. */}
            {isFreeUser ? (
              <div
                data-testid="anliegen-free-intro"
                className="bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-md p-4"
              >
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  Was willst du erreichen?
                </p>
                <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                  Beschreib kurz deine Situation — Flex4Success zeigt dir, welcher Fokustyp gerade dominiert und wie du reagieren kannst.
                </p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Free: Fokustyp-Ableitung · <b className="text-blue-800 dark:text-blue-300">Pro Solo (39 €/Monat):</b> unbegrenzt + konkreter Türöffner-Satz.
                </p>
              </div>
            ) : (
              /* Sprint 4c B.9 — Funktion klar beschreiben, keine E-Mail-Verweise, „Flexpfad schlagen" Frame */
              <div
                data-testid="anliegen-intro-section"
                className="bg-blue-50/50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-800/60 rounded-md p-4"
              >
              <p className="text-sm text-stone-700 dark:text-slate-200 leading-relaxed">
                Beschreib mir kurz deine Situation und wie der andere reagiert. Ich leite ab,
                welcher Fokustyp-Anteil in der Reaktion dominiert — und gebe dir den Satz,
                mit dem wir den Flexpfad zu ihm schlagen.
              </p>
              <p
                data-testid="anliegen-intro-helper"
                className="mt-2 text-xs text-stone-500 dark:text-slate-400"
              >
                Du kannst das für jede Alltagssituation nutzen: mit deinem Partner, im Job, in
                der Familie oder bei Behörden und Handwerkern.
              </p>
              </div>
            )}

            {/* Schritt 1: Situation */}
            <div>
              <label
                htmlFor="anliegen-situation-textarea"
                className="block text-sm font-semibold text-stone-700 dark:text-slate-200 mb-2"
              >
                Was ist deine Situation?
              </label>

              {/* Sprint V19.1 (Boardroom-CoS Mo 07.09.2026-14 Uhr Cleanup):
                  Kontext-Karten (Privat/Beruflich/Familie/Alltag) entfernt.
                  Grund: Kontext-Chip war Text-Kategorisierung, die vom
                  Heuristik-Vorfilter nicht ausgewertet wird → reine UX-Belastung
                  ohne Erkennungs-Nutzwert. Konsistent mit Free-Bereich-
                  Reaktivierung. State `kontextId` bleibt fuer moegliche
                  Post-Launch-Dropdown-Loesung erhalten. */}

              {/* Sprint 4 Issue 3.2 — Helper-Text oben (blau, klein) */}
              <p
                data-testid="situation-helper-oben"
                className="text-xs text-blue-700 font-medium mb-1"
              >
                Kurz und konkret — beschreibe, worum es geht UND wie der andere reagiert.
              </p>

              <textarea
                id="anliegen-situation-textarea"
                ref={situationRef}
                data-testid="situation-input"
                value={situation}
                onChange={handleSituationChange}
                rows={6}
                maxLength={MAX_LEN}
                placeholder="z.B. Mein Mann will nicht mit uns wegfahren. Er blockt ab, sagt 'müssen wir mal sehen' und zieht sich zurück."
                className="w-full px-4 py-3 border border-stone-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-stone-900 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* Sprint 4d AC #234 — Helper-Text + Zeichenzähler (X/1000). */}
              <p
                data-testid="situation-helper-unten"
                className="mt-1 text-xs text-stone-500 dark:text-slate-400"
              >
                Schilder die Situation konkret — je präziser, desto treffsicherer die Hinweise.
              </p>

              {/* Sprint V19.1 (Boardroom-CoS Mo 07.09.2026-14 Uhr Cleanup):
                  „Hilfe zur Formulierung — 4 Punkte" details-Toggle entfernt.
                  Der Heuristik-Vorfilter erkennt Verhalten aus Freitext direkt,
                  eine strukturierte Formulierungshilfe ist redundant und
                  belastet die UI unnötig. */}

              <div className="flex justify-end mt-1">
                <span
                  data-testid="situation-counter"
                  className={`text-xs ${situation.length >= MAX_LEN ? 'text-red-600 font-semibold' : 'text-stone-500 dark:text-slate-400'}`}
                >
                  {situation.length}/{MAX_LEN}
                </span>
              </div>

              <button
                type="button"
                data-testid="verdacht-button"
                onClick={handleVerdacht}
                disabled={verdachtLoading || situation.trim().length < 3}
                className="mt-3 inline-flex items-center px-4 py-2 bg-stone-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-md text-sm font-semibold hover:bg-stone-800 dark:hover:bg-white disabled:opacity-50"
              >
                {verdachtLoading ? 'Leite ab …' : 'Hinweise ableiten'}
              </button>

              {verdachtError && (
                <p data-testid="verdacht-error" className="text-sm text-red-700 mt-3">
                  {verdachtError}
                </p>
              )}
            </div>

            {sicherheit && <div className="mt-6"><SafetyNotice data={sicherheit} /></div>}

            {/* Sprint V19.1 (Boardroom-CoS Mo 07.09.2026-14 Uhr):
                Bei vagem Freitext (case_label=C_vage) ODER niedrigem Heuristik-
                Confidence rendern wir die V19-Folge-Frage als primäres UI.
                Die alte statische Ergänzung-nötig-Box wurde entfernt und durch
                die generische JSON-Folge-Frage ersetzt (siehe folge_fragen_v19.json). */}
            {verdacht && verdacht.case_label === 'C_vage' && folgeFrage && !verdachtOverride && (
              <FolgeFrage
                frage={folgeFrage}
                onSelect={(fokustyp) => {
                  setVerdachtOverride(fokustyp);
                  setVerdacht((prev) => prev ? {
                    ...prev,
                    verdacht_fokustyp: fokustyp,
                    case_label: 'B_klar_ueber',
                    needs_clarification: false,
                  } : prev);
                  setFolgeFrage(null);
                }}
              />
            )}

            {/* Fallback: Falls C_vage UND keine Folge-Frage geliefert wurde
                (Backend-Ausfall o.ä.), zeigen wir einen kurzen Freundlich-Hinweis. */}
            {verdacht && verdacht.case_label === 'C_vage' && (!folgeFrage || verdachtOverride) && !verdachtOverride && (
              <div
                data-testid="verdacht-box-vage-fallback"
                className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-md p-4"
              >
                <p className="text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-1">
                  Ergänzung nötig
                </p>
                <p className="text-sm text-stone-700 dark:text-slate-200">
                  Beschreib in ein bis zwei Sätzen, wie die andere Person konkret reagiert
                  — dann kann ich dir gezielt helfen.
                </p>
              </div>
            )}

            {verdacht && verdacht.case_label !== 'C_vage' && (
              <div
                data-testid="verdacht-box"
                className="bg-stone-50 dark:bg-slate-800/60 border border-stone-200 dark:border-slate-700 rounded-md p-4"
              >
                {/* Sprint V19 Schicht 2 Stufe 2 (Boardroom-CoS Di 08.09.2026-13 Uhr)
                    — Folge-Frage generisch aus JSON-Regelwerk. Wird gezeigt wenn
                    Heuristik-Vorfilter niedrigen Confidence liefert und der User
                    noch keine Wahl getroffen hat. Ueberlagert die V10-Rueckfrage
                    (nur eine der beiden erscheint gleichzeitig). */}
                {folgeFrage && !verdachtOverride && (
                  <div className="mb-4">
                    <FolgeFrage
                      frage={folgeFrage}
                      onSelect={(fokustyp) => {
                        setVerdachtOverride(fokustyp);
                        setVerdacht((prev) => prev ? {
                          ...prev,
                          verdacht_fokustyp: fokustyp,
                          needs_clarification: false,
                        } : prev);
                        setFolgeFrage(null);
                      }}
                    />
                  </div>
                )}
                {/* Sprint V10 (Boardroom-CoS 3a) — Rückfrage-Zwischenschritt bei
                    zwei möglichen Fokustypen. Erscheint nur, wenn noch keine
                    Wahl getroffen wurde. */}
                {verdacht.needs_clarification &&
                  Array.isArray(verdacht.clarification_options) &&
                  verdacht.clarification_options.length === 2 &&
                  !verdachtOverride && (
                    <div
                      data-testid="verdacht-clarification"
                      className="mb-4 rounded-md border border-blue-200 dark:border-[#7A92D4]/50 bg-blue-50 dark:bg-[#1A2440] p-3"
                    >
                      <p className="text-xs font-bold uppercase tracking-widest text-blue-700 dark:text-[#9DB0E4] mb-2">
                        Coaching-Rückfrage
                      </p>
                      <p className="text-sm text-stone-800 dark:text-slate-200 leading-relaxed">
                        Ich sehe zwei mögliche Muster in dieser Reaktion. Welches trifft es besser?
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {verdacht.clarification_options.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`verdacht-clarification-choice-${opt}`}
                            onClick={() => {
                              setVerdachtOverride(opt);
                              setVerdacht((prev) => prev ? {
                                ...prev,
                                verdacht_fokustyp: opt,
                                needs_clarification: false,
                              } : prev);
                            }}
                            className="px-3 py-1.5 rounded-full border border-blue-400 bg-white dark:bg-slate-900 text-sm font-semibold text-blue-800 dark:text-[#9DB0E4] hover:bg-blue-100 dark:hover:bg-slate-800 transition-colors"
                          >
                            Eher {FOKUSTYP_LABEL[opt] || opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                <p className="text-xs font-bold uppercase tracking-widest text-stone-500 dark:text-slate-400 mb-1">
                  Was du in seiner Reaktion erkennst
                </p>
                <p className="text-stone-900 dark:text-white font-semibold">
                  {verdacht.case_label === 'B_tendenz' ? (
                    <span data-testid="verdacht-tendenz-lead">
                      In seiner Reaktion zeigen sich zwei Spuren — vor allem dominiert gerade der{' '}
                      <span data-testid="verdacht-fokustyp">
                        {FOKUSTYP_LABEL[verdacht.verdacht_fokustyp] || '—'}
                      </span>
                      -Anteil.
                    </span>
                  ) : (
                    <>
                      In seiner Reaktion dominiert gerade der{' '}
                      <span data-testid="verdacht-fokustyp">
                        {FOKUSTYP_LABEL[verdacht.verdacht_fokustyp] || '—'}
                      </span>
                      -Anteil.
                    </>
                  )}
                </p>
                {VERDACHT_HELPER_BY_FOKUSTYP[verdacht.verdacht_fokustyp] && (
                  <p
                    data-testid="verdacht-helper"
                    className="mt-1 text-xs text-stone-500 dark:text-slate-400 italic"
                  >
                    {VERDACHT_HELPER_BY_FOKUSTYP[verdacht.verdacht_fokustyp]}
                  </p>
                )}
                <p
                  data-testid="verdacht-begruendung"
                  className="text-sm text-stone-700 dark:text-slate-200 mt-2"
                >
                  {verdacht.verdacht_begruendung}
                </p>

                {/* Sprint V28 Issue H (Fr 27.02.2026) — Andreas-Differenzierungs-
                    Leitlinie: Free-User bekommt fokustyp-spezifische Handlungs-
                    Empfehlung mit klarem DO + DON'T. Vermeidet identische Tipps
                    fuer psychologisch verschiedene Typen (bes. HUND vs KATZE).
                    Sales-Hebel bleibt via Pro-Solo-Hinweis intakt. */}
                {isFreeUser && FREE_HANDLUNGS_TIPP_BY_FOKUSTYP[verdacht.verdacht_fokustyp] && (
                  <div
                    className="mt-4 rounded-lg border border-blue-100 dark:border-slate-700 bg-blue-50/60 dark:bg-slate-900/60 p-3"
                    data-testid="free-handlungs-tipp"
                    data-fokustyp={verdacht.verdacht_fokustyp}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-800 dark:text-blue-300">
                      Was jetzt konkret hilft
                    </p>
                    <p
                      className="mt-1.5 text-sm text-slate-800 dark:text-slate-100 leading-snug"
                      data-testid="free-handlungs-tipp-do"
                    >
                      <span className="font-bold text-emerald-700 dark:text-emerald-400">✓ </span>
                      {FREE_HANDLUNGS_TIPP_BY_FOKUSTYP[verdacht.verdacht_fokustyp].do}
                    </p>
                    <p
                      className="mt-1.5 text-sm text-slate-800 dark:text-slate-100 leading-snug"
                      data-testid="free-handlungs-tipp-dont"
                    >
                      <span className="font-bold text-rose-700 dark:text-rose-400">✗ </span>
                      {FREE_HANDLUNGS_TIPP_BY_FOKUSTYP[verdacht.verdacht_fokustyp].dont}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/pricing")}
                      data-testid="free-handlungs-tipp-pro-cta"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-800 dark:text-blue-300 hover:underline"
                    >
                      Mit Pro Solo: der vollständige Flexpfad mit exakter Formulierung →
                    </button>
                  </div>
                )}

                {/* 2 Pfade */}
                <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                  <button
                    type="button"
                    data-testid="quick-path-button"
                    onClick={() => runGenerate('quick')}
                    disabled={generating}
                    className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all inline-flex items-center gap-2 ${
                      generating
                        ? 'bg-stone-300 dark:bg-slate-700 text-stone-500 dark:text-slate-400 cursor-not-allowed'
                        : 'bg-blue-700 text-white hover:bg-blue-800'
                    }`}
                  >
                    {generating && <Loader2 className="w-4 h-4 animate-spin" />}
                    {generating ? 'Generiert … (~10 Sek)' : 'Formel direkt generieren'}
                  </button>
                  {/* Sprint V10.1 (Boardroom-CoS Sa 05.-17 Uhr): Praezise-Toggle
                      entfernt — mit der V10-Rueckfrage-Funktion ist die
                      separate Präzise-Erfassung obsolet. */}
                </div>
              </div>
            )}

            {/* Sprint V12 Fix 2 (Boardroom-CoS Sa 05.09.2026-18 Uhr):
                StarModellHelfer (6 Ebenen Kommunikations-Framework — Variante 2)
                gehoert NICHT in den Anliegen-Flow, sondern ausschliesslich in
                PageWirkungGestalten.jsx. Hier war er faelschlich integriert.
                Der Anliegen-Flow zeigt stattdessen nur den 4-Punkte-Eskalations-
                Framework-Hint unter der Textarea (siehe <details data-testid=
                "star-modell-hint">). */}

            {/* Schritt 2 Präzise — progressiv */}
            {verdacht && showPraezise && (
              <div
                data-testid="praezise-block"
                className="border-t border-stone-200 dark:border-slate-700 pt-6 space-y-4"
              >
                {praeziseStep === 1 && (
                  <div data-testid="logisch-frage-block">
                    <label
                      data-testid="logisch-label"
                      className="block text-sm font-semibold text-stone-700 dark:text-slate-200 mb-2"
                    >
                      Worum geht's dir eigentlich?
                    </label>
                    <textarea
                      data-testid="logisch-input"
                      value={logisch}
                      onChange={(e) => setLogisch(e.target.value)}
                      rows={3}
                      placeholder="z.B. Es gibt keinen konkreten Plan, der ihn überzeugt. Er sagt, das bringt doch nichts."
                      className="w-full px-4 py-3 border border-stone-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-stone-900 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      data-testid="logisch-weiter"
                      disabled={logisch.trim().length < 3}
                      onClick={() => setPraeziseStep(2)}
                      className="mt-3 px-5 py-2 bg-stone-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-md text-sm font-semibold hover:bg-stone-800 dark:hover:bg-white disabled:opacity-50"
                    >
                      Weiter
                    </button>
                  </div>
                )}

                {praeziseStep === 2 && (
                  <div data-testid="emotional-frage-block">
                    <label
                      data-testid="emotional-label"
                      className="block text-sm font-semibold text-stone-700 dark:text-slate-200 mb-2"
                    >
                      Was löst das bei dir aus?
                    </label>
                    <textarea
                      data-testid="emotional-input"
                      value={emotional}
                      onChange={(e) => setEmotional(e.target.value)}
                      rows={3}
                      placeholder="z.B. Ich hab das Gefühl, er will seine Ruhe. Wir hatten das öfter."
                      className="w-full px-4 py-3 border border-stone-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-stone-900 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      data-testid="generate-praezise"
                      disabled={generating || emotional.trim().length < 3}
                      onClick={() => runGenerate('praezise')}
                      className="mt-3 px-5 py-2.5 bg-blue-700 text-white rounded-md text-sm font-semibold hover:bg-blue-800 disabled:opacity-50"
                    >
                      {generating ? 'Generiert … (~10 Sek)' : 'Formel generieren'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {generateError && (
              <div
                data-testid="generate-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
              >
                {generateError}
              </div>
            )}

            {generating && (
              <div
                data-testid="anliegen-loading"
                className="space-y-4"
                role="status"
                aria-live="polite"
              >
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 dark:text-slate-200 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 rounded-lg px-4 py-2.5">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-700 dark:text-blue-300" />
                  <AnliegenLoadingPhase />
                </div>
                <AnliegenSkeleton />
              </div>
            )}
          </section>
        )}

        {!isFreeUser && result && (
          <div data-testid="anliegen-output" className="space-y-6">
            <BegegnungVisual begegnung={result.begegnung} isPro />
            {result.kern_antwort && (
              <section data-testid="pro-kern-antwort" className="bg-white dark:bg-slate-900 border border-stone-200 dark:border-slate-800 rounded-lg p-6">
                <KernAntwort antwort={result.kern_antwort} />
              </section>
            )}
            <section
              data-testid="verdacht-card"
              className="bg-white dark:bg-slate-900 border border-stone-200 dark:border-slate-800 rounded-lg p-6"
              style={{
                borderTop: `4px solid ${
                  (result.situations_karten || []).find((b) => b.fokustyp === result.verdacht_fokustyp)
                    ?.color || '#15803D'
                }`,
              }}
            >
              <p className="text-xs font-bold uppercase tracking-widest text-stone-500 dark:text-slate-400 mb-2">
                Hinweise
              </p>
              {/* Sprint V23 B1 (Mi09.09.2026) — neutrales Pronomen-Rendering fuer
                  generische Rollenterme (Partner/Freund/Mitarbeiter/Eltern/Kind).
                  Fallback maskuliner Default nur bei explizit maennlichen Formen. */}
              {(() => {
                const poss = (result.v21_pronoun || {}).possessiv;
                const typLabel = FOKUSTYP_LABEL[result.verdacht_fokustyp] || '—';
                let prefix;
                if (poss === 'ihr') prefix = 'In ihrer Reaktion dominiert gerade der ';
                else if (poss === 'sein') prefix = 'In seiner Reaktion dominiert gerade der ';
                else prefix = 'In der Reaktion dieser Person dominiert gerade der ';
                return (
                  <h2
                    className="text-2xl font-bold text-stone-900 dark:text-white mb-2"
                    data-testid="result-verdacht-headline"
                  >
                    {prefix}
                    <span data-testid="result-verdacht-fokustyp">{typLabel}</span>
                    -Anteil.
                  </h2>
                );
              })()}
              {/* Sprint V23 B2 (Mi09.09.2026) — Sub-Dimension als zweiter Satz einblenden.
                  Zeigt Klaerungsblockade / Antwort-Ausstehend / Entscheidung-Ausstehend
                  (BIENE) bzw. Reflexion-Laueft / Perspektiv-Wechsel / Integration-Noetig
                  (EULE). Nur wenn v22_sub_dimension_top vorhanden ist. */}
              {result.v22_sub_dimension_top && (
                <p
                  data-testid="result-sub-dimension"
                  className="text-sm text-stone-700 dark:text-slate-200 italic mb-2"
                >
                  {(() => {
                    const map = {
                      klaerung_blockiert: 'Aktuell blockiert durch fehlende Klarheit — es wartet auf Info, Anleitung oder Bestaetigung.',
                      antwort_ausstehend: 'Aktuell wartet eine konkrete Antwort oder Rueckmeldung.',
                      entscheidung_ausstehend: 'Aktuell steht eine Entscheidung von jemand anderem aus.',
                      reflexion_laeuft: 'Aktuell laueft innere Verarbeitung — braucht Zeit zum Reifen.',
                      perspektiv_wechsel: 'Aktuell wird eine neue Sichtweise abgewogen.',
                      integration_noetig: 'Aktuell muessen Widersprueche kognitiv aufgeloest werden.',
                    };
                    return map[result.v22_sub_dimension_top] || null;
                  })()}
                </p>
              )}
              <p data-testid="result-verdacht-begruendung" className="text-stone-700 dark:text-slate-200">
                {result.verdacht_begruendung}
              </p>
              {/* Sprint 11 Light — Confidence-Indikator */}
              {result.confidence_quelle && (
                <div className="mt-3">
                  <ConfidenceBadge
                    quelle={result.confidence_quelle}
                    confidence={result.confidence}
                    hinweis={result.confidence_hinweis}
                  />
                </div>
              )}
            </section>

            <section
              data-testid="tuersteher-insight"
              className="bg-blue-50 dark:bg-[#1A2440] border-l-4 border-blue-700 dark:border-[#7A92D4] rounded p-5"
            >
              <p className="text-xs font-bold uppercase tracking-widest text-blue-900 dark:text-[#9DB0E4] mb-2">
                Was ich erkenne
              </p>
              <p className="text-stone-900 dark:text-slate-100 italic">{result.tuersteher_insight}</p>
            </section>

            {/* Sprint 10 — Flexpfad-Bibliothek V1 (4-Sektionen-Architektur mit Outcome-Benefit).
                Steht zwischen Türsteher-Insight und Flexpfad-Block — gibt dem User
                das "Warum-das-wirkt" + 3 Stellschrauben + Mikro-Challenge. */}
            {result.heart_piece && <HeartPiece heartPiece={result.heart_piece} />}

            {/* Sprint 4c-4 D.1 — Flexpfad-Block (ersetzt 4 Situations-Karten als Default).
                Disclosure-Toggle direkt darunter für Einzel-Phrasen (Lerneffekt). */}
            {result.bridge_block && (
              <>
                <BrueckensatzBlock
                  bridge={result.bridge_block}
                  modus={result.modus}
                  testid="bruecke-block"
                />
                {/* Sprint V12 Fix 6 (Boardroom-CoS Sa 05.09.2026-18 Uhr) —
                    Output-Laenge auf 3-5 Saetze reduziert: statt aller 4
                    Vorbereitungs-Zeilen zeigen wir nur die Top 2 (der
                    dominante Fokustyp + der zweitstaerkste). Bringt uns von
                    7 → 5 Saetzen im Gesamtoutput. */}
                <BrueckensatzToggle
                  phrasen={(result.einzel_phrasen || []).slice(0, 2)}
                  verdachtFokustyp={result.verdacht_fokustyp}
                  testid="bruecke-toggle"
                />
              </>
            )}

            {/* Sprint 4c-4 D.1 — Alternative-Hint (nur bei Case B Tendenz) */}
            {result.alternative_hint && (
              <section
                data-testid="alternative-hint"
                data-alternative-fokustyp={result.alternative_fokustyp || ''}
                className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-lg p-4"
              >
                <p className="text-xs font-bold uppercase tracking-widest text-amber-800 dark:text-amber-300 mb-1">
                  💡 Alternative
                </p>
                <p className="text-sm text-stone-800 dark:text-slate-200 leading-relaxed">
                  {result.alternative_hint}
                </p>
              </section>
            )}

            {/* Sprint 4c-2 A.9 — Newsletter-Opt-in nur fuer Free-User unter Co-Creator-Output.
                Wichtig: isFreeUser ist NUR true wenn Tier explizit geladen und kein Premium.
                Während Loading-State (isPremium === null) wird NICHTS gerendert (verhindert Flash). */}
            {isFreeUser && <KlickTippOptin quelle="anliegen-co-creator-output" />}

            {/* Sprint 4c-2 A.10 — Pro-Solo-Upsell-CTA nur fuer Free-User.
                Gleicher Schutz: nur explizit Free-User sehen das Upsell. */}
            {isFreeUser && (
              <section
                data-testid="upsell-block"
                className="bg-stone-100 dark:bg-slate-800/40 border border-dashed border-stone-300 dark:border-slate-600 rounded-lg p-6 text-center"
              >
                <p className="text-sm text-stone-600 dark:text-slate-300 mb-2">Mehr Tiefe gewünscht?</p>
                <button
                  data-testid="upsell-cta"
                  onClick={() => navigate('/dashboard/abo')}
                  className="bg-white dark:bg-slate-900 border-2 border-blue-700 text-blue-700 dark:text-blue-400 px-6 py-2 rounded-md font-semibold hover:bg-blue-700 hover:text-white dark:hover:bg-blue-600 transition"
                >
                  {BRAND.name} Pro Solo · 39 €/Monat
                </button>
              </section>
            )}

            <button
              data-testid="anliegen-reset"
              onClick={reset}
              className="text-sm text-stone-600 dark:text-slate-300 hover:text-stone-900 dark:text-white"
            >
              ← Neue Situation formulieren
            </button>

            {/* Sprint 5a AC #3 — Saved Library: Save-Toggle für aktuellen Flexpfad */}
            {result.run_id && (
              <div className="flex items-center gap-3" data-testid="anliegen-save-block">
                <button
                  type="button"
                  data-testid="anliegen-save-toggle"
                  disabled={savingToggle}
                  onClick={async () => {
                    setSavingToggle(true);
                    setSaveError(null);
                    try {
                      const res = await api.post(`/anliegen/${result.run_id}/save`);
                      setResultSaved(Boolean(res.data?.saved));
                    } catch (err) {
                      setSaveError(err?.response?.data?.detail || 'Speichern fehlgeschlagen.');
                    } finally {
                      setSavingToggle(false);
                    }
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                    resultSaved
                      ? 'bg-blue-700 text-white border-2 border-blue-700 hover:bg-blue-800'
                      : 'bg-white text-blue-700 border-2 border-blue-700 hover:bg-blue-50'
                  } ${savingToggle ? 'opacity-60 cursor-wait' : ''}`}
                >
                  {resultSaved ? '✓ In meiner Bibliothek' : '☆ In Bibliothek speichern'}
                </button>
                {saveError && (
                  <span data-testid="anliegen-save-error" className="text-xs text-red-600">
                    {saveError}
                  </span>
                )}
              </div>
            )}

            {result.latency_ms != null && (
              <p className="text-xs text-stone-400 text-right">
                Generiert in {(result.latency_ms / 1000).toFixed(1)} s
                {result.sanitizer_retries ? ` · ${result.sanitizer_retries} Retry` : ''}
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
